import ToggleGroup, {
  ToggleButton,
  ToggleButtonSize,
} from '@/components/ui/custom/dataInput/ToggleGroup';
import { BusinessModelEnum, CourseFormatEnum } from '@/models/Course';
import { compact } from 'lodash';
import { useTranslation } from 'react-i18next';

type FilterType = 'all' | 'format' | 'business';

type FilterOption = {
  label: string;
  type: FilterType;
  value?: CourseFormatEnum | BusinessModelEnum;
  key: string;
};

type CourseCategoryFilterFilter = {
  formats?: CourseFormatEnum[];
  businessModels?: BusinessModelEnum[];
};

type CourseCategoryFilterProps = {
  filter?: CourseCategoryFilterFilter;
  onFilter?: (filters: CourseCategoryFilterFilter) => void;
  size?: ToggleButtonSize;
};

export default function CourseCategoryFilter({
  filter,
  onFilter,
  size,
}: CourseCategoryFilterProps) {
  const [tFormats] = useTranslation('translation', {
    keyPrefix: 'courseFormats',
  });
  const [tBusiness] = useTranslation('translation', {
    keyPrefix: 'businessModels',
  });
  const [t] = useTranslation('translation', {
    keyPrefix: 'coursesView.admin.filters',
  });

  const filterOptions: FilterOption[] = [
    { label: t('all'), type: 'all', key: 'all' },
    {
      label: tFormats(CourseFormatEnum.IN_PERSON),
      type: 'format',
      value: CourseFormatEnum.IN_PERSON,
      key: `format:${CourseFormatEnum.IN_PERSON}`,
    },
    {
      label: tFormats(CourseFormatEnum.ONLINE),
      type: 'format',
      value: CourseFormatEnum.ONLINE,
      key: `format:${CourseFormatEnum.ONLINE}`,
    },
    {
      label: tFormats(CourseFormatEnum.EXTRA_COURSE),
      type: 'format',
      value: CourseFormatEnum.EXTRA_COURSE,
      key: `format:${CourseFormatEnum.EXTRA_COURSE}`,
    },
    {
      label: tBusiness(BusinessModelEnum.IN_SCHOOL),
      type: 'business',
      value: BusinessModelEnum.IN_SCHOOL,
      key: `business:${BusinessModelEnum.IN_SCHOOL}`,
    },
    {
      label: tBusiness(BusinessModelEnum.CNA_STANDARD),
      type: 'business',
      value: BusinessModelEnum.CNA_STANDARD,
      key: `business:${BusinessModelEnum.CNA_STANDARD}`,
    },
    {
      label: tFormats(CourseFormatEnum.SELF_PACED),
      type: 'format',
      value: CourseFormatEnum.SELF_PACED,
      key: `format:${CourseFormatEnum.SELF_PACED}`,
    },
  ];

  const buttons: ToggleButton<string>[] = filterOptions.map(option => ({
    text: option.label.toLowerCase(),
    value: option.key,
  }));

  const getCurrentKey = (): string[] => {
    const formatsKey = filter?.formats?.map(format => `format:${format}`) || [];
    const businessModelsKey =
      filter?.businessModels?.map(
        businessModel => `business:${businessModel}`,
      ) || [];
    if (!formatsKey.length && !businessModelsKey.length) {
      return ['all'];
    }
    return [...formatsKey, ...businessModelsKey];
  };

  const mapKey = <T extends CourseFormatEnum | BusinessModelEnum>(
    key: string,
  ): T | undefined => {
    const option = filterOptions.find(opt => opt.key === key);
    if (!option) return;
    return option.value as T;
  };

  const shouldSelectAll = (keys: string[]): boolean => {
    const currentKey = getCurrentKey();
    return !currentKey.includes('all') && keys.includes('all');
  };

  const segregateKeys = (
    keys: string[],
  ): { formats: string[]; businessModels: string[] } => {
    return {
      formats: keys.filter(key => key.startsWith('format:')),
      businessModels: keys.filter(key => key.startsWith('business:')),
    };
  };

  const convertKeysToValues = <T extends CourseFormatEnum | BusinessModelEnum>(
    keys: string[],
  ): T[] => {
    return compact(keys.map(key => mapKey<T>(key))) as T[];
  };

  const onChange = (keys: string[]) => {
    const filteredKeys = keys.filter(key => key !== 'all');

    if (shouldSelectAll(keys) || filteredKeys.length === 0) {
      onFilter?.({
        formats: undefined,
        businessModels: undefined,
      });
      return;
    }

    const { formats, businessModels } = segregateKeys(filteredKeys);
    onFilter?.({
      formats: formats.length
        ? convertKeysToValues<CourseFormatEnum>(formats)
        : undefined,
      businessModels: businessModels.length
        ? convertKeysToValues<BusinessModelEnum>(businessModels)
        : undefined,
    });
  };

  return (
    <ToggleGroup
      buttons={buttons}
      value={getCurrentKey()}
      onChange={onChange}
      size={size || 'medium'}
    />
  );
}
